import { createSelector } from 'reselect';
import _ from 'lodash';
import { List } from 'immutable';
import thicknessDisplayName from 'domain/Options/thicknessDisplayName';
import { WALL_EDGING_STRIP_ID, WALL_EDGING_STRIP_CUSTOMIZABLE_ID, NONE as NO_SPLASHBACK_ID } from 'domain/Options/generators/splashbacks';
import { NO_WORKTOP_MATERIAL } from 'domain/Options/generators/materials';
import { validateSinkConditions, getSinkLengthCondition } from 'pages/MainPage/PlanPage/shapeMeasurementsError';
import { NO_KITCHEN_ISLAND_ID } from 'domain/Options/generators/kitchenIslands';
import { NO_OVERHANG_ID } from 'domain/Options/generators/overhangs';
import { optionsSelector, noWallpanelsAvailableSelector, useCoverPanelsSelector  } from '../selectors';
import { isExpressionDecentralizedSelector, isKitchenIslandExpressionDecentralizedSelector } from '../selectors/parameters';
import { maxDepthSelector } from './measurements';
import { worktopOperationParameters, kitchenIslandOperationParameters, wallPanelOperationParameters, planOperationParameters } from './operations';
import * as parameters from './parameters';
import { SELECTION_TYPE_USER } from '../selectionTypes';
import parameterMessages from '../parameterMessages';
import createFieldSelector from './utils/createFieldSelector';
import edgeMessages from 'domain/Options/generators/messages/edgeMessages';
import overhangMessages from 'domain/Options/generators/messages/overhangMessages';
import { isQuartzAndCeramicDecentralized } from 'domain/Options/itemList';

export const availableShapesSelector = createSelector(
  parameters.materialParameterSelector,
  optionsSelector,
  maxDepthSelector,
  parameters.expressionParameterSelector,
  parameters.sinkFasteningParameterSelector,
  parameters.sinkModelParameterSelector,
  (materialParameter, options, maxDepth, expressionParameter, sinkFasteningParameter, sinkModel) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!materialSelected) {
      return options.get('shapes');
    }
    return options.get('shapes').map((shape) => {
      const conflicts = [];
      const conditions = shape.get('conditions');
      const sinkFasteningValue = sinkFasteningParameter.get('value');
      const sinkFasteningCondition = shape.get('sinkFasteningCondition');
      const sinkModelValue = sinkModel.get('value');

      if (conditions && !conditions({ maxDepth })) {
        conflicts.push(expressionParameter.getIn(['value', 'displayName']));
      }
      if (sinkFasteningValue && sinkFasteningCondition && !sinkFasteningCondition(sinkFasteningValue.get('id'))) {
        conflicts.push(sinkFasteningValue.get('displayName'));
      }
      if (shape.get('id') === 'irregular' && sinkModelValue && sinkModelValue.getIn(['fastenings', 'fitBetween']) && sinkModelValue.get('fastenings').size === 1) {
        conflicts.push(sinkModelValue.get('displayName'));
      }

      if (conflicts.length > 0) {
        return shape.set('conflicts', List(conflicts));
      } else {
        return shape;
      }
    });
  }
);

const isEdgeIncluded = (material) => {
  if(material.getIn(['edges']) === 'noEdgeIncluded'){
    return false;
  } else {
    return true;
  }
};
const edgeMaterialAllowed = (material, edgeValue) => {
  return material.getIn(['edges']).find((materialEdge) => materialEdge.get('value') === edgeValue);
};

const thicknessAvailableInExpression = (expression, thicknessId) => {
  return expression.get('thicknesses').find((expressionThickness) => expressionThickness.get('id') === thicknessId);
};

const thicknessAvailableWithSinkModel = (sinkModel, thickness) => {
  const thicknessCondition = sinkModel.getIn(['configuration', 'thicknessCondition']);
  return !thicknessCondition || thicknessCondition({ thickness });
};

export const availablekitchenIslandsSelector = createSelector(
  optionsSelector,
  (options) => options.get('kitchenIslands'),
);

export const firstAvailableShapeIdSelector = createSelector(
  optionsSelector,
  (options) => {
    let firstID;
    options.get('shapes').find((shape) => {
      if (shape.get('id') && !firstID) {
        firstID = shape.get('id');
      }
    });
    return firstID;
  }
);

export const firstAvailableMaterialIdSelector = createSelector(
  optionsSelector,
  (options) => {
    let firstID;
    options.get('materials').filter((material) => material.get('id') !== NO_WORKTOP_MATERIAL).find((material) => {
      if (material.get('id') && !firstID) {
        firstID = material.get('id');
      }
    });
    return firstID;
  }
);

export const availableMaterialsSelector = createSelector(
  parameters.materialParameterSelector,
  optionsSelector,
  parameters.edgeParameterSelector,
  parameters.drainerGroovesParameterSelector,
  parameters.sinkMaterialParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.sinkFasteningParameterSelector,
  parameters.thicknessParameterSelector,
  parameters.selectedThicknessSelector,
  parameters.plannedShapeParameterSelector,
  parameters.splashbackParameterSelector,
  parameters.overhangParameterSelector,
  createFieldSelector([...worktopOperationParameters, ...planOperationParameters], 'parameter'),
  parameters.expressionParameterSelector,
  parameters.isExpressionDecentralizedSelector,
  (materialParameter, options, edgeParameter, drainerGroovesParameter, sinkMaterialParamter, sinkModelParameter, sinkFasteningParameter, thicknessParameter, selectedThickness, shapeParameter, splashbackParameter, overhangParameter, operations, expressionParameter, isDecentralized) => {
    const edgeOption = edgeParameter.get('value');
    const drainerGrooves = drainerGroovesParameter.get('value');
    const sinkMaterial = sinkMaterialParamter.get('value');
    const sinkModel = sinkModelParameter.get('value');
    const sinkModelName = sinkModel && sinkModel.get('displayName');
    const sinkModelFastenings = sinkModel && sinkModel.get('fastenings');
    const sinkFastening = sinkFasteningParameter.get('value');
    const splashback = splashbackParameter.get('value');
    const overhang = overhangParameter.get('value');

    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!materialSelected && splashback) {
      return options.get('materials').map((material) => {
        const conflicts = [];
        if (splashback.get('id') === WALL_EDGING_STRIP_ID && material.id === NO_WORKTOP_MATERIAL) {
          conflicts.push(material.id);
        }
        if (splashback.get('id') === WALL_EDGING_STRIP_CUSTOMIZABLE_ID && material.id === NO_WORKTOP_MATERIAL) {
          conflicts.push(material.id);
        }
        if (conflicts.length > 0) {
          return material.set('conflicts', List(conflicts));
        } else {
          return material;
        }
      });
    }
    if (!edgeOption || !drainerGrooves || !sinkMaterial || !sinkModel || !sinkFastening || !splashback || !overhang) return options.get('materials');

    return options.get('materials').map((material) => {
      const conflicts = [];
      if (material.get('id') === NO_WORKTOP_MATERIAL) {
        return material;
      }
      if(isEdgeIncluded(material)){
        if (!edgeMaterialAllowed(material, edgeOption.get('id'))) {
          if(edgeOption.get('id') === 'same-color' && isDecentralized){
            const edgeOptionStraight = edgeOption.setIn(['displayName', 'id'], 'cwt.edges.straight');
            conflicts.push(edgeOptionStraight.get('displayName'));
          } else {
            conflicts.push(edgeOption.get('displayName'));
          }
        }
        if(material.get('id') === 'quartz' || material.get('id') === 'ceramic'){    
          const selectedEdgeValue = edgeOption && edgeOption.get('id');           
          if((selectedEdgeValue !== 'chamfered') && (overhang.get('id') === 'waterfall')){                    
            selectedEdgeValue === 'same-color' ?  conflicts.push(edgeMessages.straight) : conflicts.push(edgeOption.get('displayName')); 
            conflicts.push(overhang.get('displayName')); 
          }
        }
      }
    
      if (drainerGrooves.get('id') !== 'none' && !material.getIn(['operations', 'drainerGrooves'])) {
        conflicts.push(drainerGrooves.get('displayName'));
      }

      if (sinkMaterial.get('id') !== 'noSink') {
        const undergluedFastenings = sinkModelFastenings.get('underGlued') || List();
        const underglueInThisMaterial = undergluedFastenings.find((s) => s.get('worktopMaterialId') === material.get('id')) || false;
        const canBeTopMounted = sinkModelFastenings.get('topMounted');
        const canBeFitBetween = sinkModelFastenings.get('fitBetween');
        const hasOtherMounting = canBeTopMounted || canBeFitBetween;

        if (!underglueInThisMaterial && !hasOtherMounting) {
          conflicts.push(sinkModel.get('displayName'));
        } else if (sinkFastening.get('id') === 'underGlued' && !underglueInThisMaterial) {
          conflicts.push(sinkFastening.get('displayName'));
        }
      }

      if (splashback.get('id') === WALL_EDGING_STRIP_ID && !material.get('wallEdgingStrip')) {
        conflicts.push(splashback.get('displayName'));
      }

      if (splashback.get('id') === WALL_EDGING_STRIP_CUSTOMIZABLE_ID && !material.get('wallEdgingStripCustomizable')) {
        conflicts.push(splashback.get('displayName'));
      }

      const allowedMaterials = overhang.get('allowedMaterials');
      if (allowedMaterials && !allowedMaterials.includes(material.get('id'))) {
        conflicts.push(overhang.get('displayName'));
      }

      operations.forEach((operation) => {
        if (!material.getIn(['operations', operation.operation]) && operation.parameter.get('value') > 0 && operation.operation !== 'highCabinetJoint') {
          conflicts.push(operation.message);
        }
      });

      // Conflict with thickness or shape if no expression available with current thickness and/or shape.
      const shapeName = shapeParameter.getIn(['value', 'displayName']);
      const selectedThicknessOrId = (selectedThickness && selectedThickness.get('thickness')) || thicknessParameter.getIn(['value', 'id']);
      const thicknessName = thicknessDisplayName(selectedThicknessOrId);
      const expressions = expressionsForMaterial(material, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, expressionParameter);
      const noExpressionsAvailableWithShape = expressions.every((expr) => expr.get('conflicts') && expr.get('conflicts').includes(shapeName));
      const noExpressionsAvailableWithThickness = expressions.every((expr) => {
        return expr.get('conflicts') && expr.get('conflicts').some((c) => _.isEqual(c, thicknessName));
      });
      const noExpressionsAvailableWithSinkModel = sinkModel && !expressions.some((expr) => !(expr.get('conflicts') && expr.get('conflicts').includes(sinkModelName)));

      if (noExpressionsAvailableWithShape) {
        conflicts.push(shapeName);
      }

      if (noExpressionsAvailableWithThickness) {
        conflicts.push(thicknessName);
      }

      if (noExpressionsAvailableWithSinkModel) {
        conflicts.push(sinkModelName);
      }

      if (conflicts.length > 0) {
        return material.set('conflicts', List(conflicts));
      } else {
        return material;
      }
    });
  }
);

const expressionsForMaterial = (material, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, expressionParameter) => {
  const selectedThicknessOptionValue = thicknessParameter.get('value');
  const shape = shapeParameter.get('value');
  const expressions = material.get('expressions');
  const sinkModel = sinkModelParameter.get('value');


  if (!expressions) {
    return List();
  }

  return expressions.map((expression) => {
    const conflicts = [];
    if (isEdgeIncluded(material)) {
      const expToIgnore = getListOfItemsToIgnore(material);
      const edgeValue = edgeParameter && edgeParameter.get('value').get('id');
      Object.keys(expToIgnore).forEach((key) => {
        if (edgeMaterialAllowed(material, edgeValue) && (expToIgnore[key].includes(expression.get('itemNo')) && edgeValue === key)) {
          conflicts.push(edgeValue);
        }
      });
    }
    if (selectedThicknessOptionValue && thicknessParameter.get('selectionType') === SELECTION_TYPE_USER && !thicknessAvailableInExpression(expression, selectedThicknessOptionValue.get('id'))) {
      conflicts.push(thicknessDisplayName(selectedThicknessOptionValue.get('id')));
    }
    const shapeConditions = shape && shape.get('conditions');
    if (shapeParameter.get('selectionType') === SELECTION_TYPE_USER && shapeConditions) {
      const maxDepth = expression.getIn(['thicknesses', 0, 'depths']).reduce((max, depthObject) => depthObject.get('maxDepth') > max ? depthObject.get('maxDepth') : max, 0);
      if (!shapeConditions({ maxDepth })) {
        conflicts.push(shape.get('displayName'));
      }
    }
    if (sinkModel && !expression.get('thicknesses').some((thicknes) => thicknessAvailableWithSinkModel(sinkModel, thicknes.get('id')))) {
      conflicts.push(sinkModel.get('displayName'));
    }
    if (conflicts.length > 0) {
      return expression.set('conflicts', List(conflicts));
    } else {
      return expression;
    }
  });
};

const coverPanelsForMaterial = (material, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, expressionParameter) => {
  const coverpanels = material.get('coverpanels');

  if (!coverpanels) {
    return List();
  }
  return coverpanels.map((coverpanel) => {
    return coverpanel;
  });
};

export const availableExpressionsSelector = createSelector(
  parameters.materialParameterSelector,
  parameters.thicknessParameterSelector,
  parameters.plannedShapeParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.edgeParameterSelector,
  parameters.expressionParameterSelector,
  (materialParameter, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, expressionParameter) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const materialValue = materialParameter.get('value');
    if (materialParameter.get('value') === null) {
      return List();
    } else if (!materialSelected) {
      return materialValue.get('expressions');
    }

    return expressionsForMaterial(materialParameter.get('value'), thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, expressionParameter);
  }
);

export const availableCoverPanelsSelector = createSelector(
  useCoverPanelsSelector,
  parameters.materialParameterSelector,
  parameters.thicknessParameterSelector,
  parameters.plannedShapeParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.edgeParameterSelector,
  parameters.coverPanelParameterSelector,
  (useCoverPanels, materialParameter, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, coverPanelParameter) => {
    if (!useCoverPanels || useCoverPanels === undefined){
      return List();
    }

    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const materialValue = materialParameter.get('value');
    if (materialParameter.get('value') === null) {
      return List();
    } else if (!materialSelected) {
      return List();
    } else {
      return materialValue.get('coverpanels');
    }
    return coverPanelsForMaterial(materialParameter.get('value'), thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, coverPanelParameter);
  }
);

export const availableKitchenIslandExpressionsSelector = createSelector(
  parameters.kitchenIslandMaterialParameterSelector,
  parameters.kitchenIslandThicknessParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.kitchenIslandEdgeParameterSelector,
  parameters.kitchenIslandExpressionParameterSelector,
  (materialParameter, thicknessParameter, kitchenIsland, edgeParameter, expressionParameter) => {
    const kitchenIslandSelected = kitchenIsland.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    const materialValue = materialParameter.get('value');

    if (materialValue === null) {
      return List();
    } else if (!kitchenIslandSelected) {
      return materialValue.get('expressions');
    }

    return kitchenIslandExpressionsForMaterial(materialValue, thicknessParameter, edgeParameter, expressionParameter);
  }
);


export const kitchenIslandCoverPanelsForMaterial = (material, thicknessParameter, edgeParameter, expressionParameter) => {
  const coverpanels = material.get('coverpanels');
  if (!coverpanels) {
    return List();
  }
  return coverpanels.map((coverpanel) => {
    return coverpanel;

  });
};


export const availableKitchenIslandCoverPanelsSelector = createSelector(
  useCoverPanelsSelector,
  parameters.kitchenIslandMaterialParameterSelector,
  parameters.kitchenIslandThicknessParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.kitchenIslandEdgeParameterSelector,
  parameters.kitchenIslandExpressionParameterSelector,
  parameters.kitchenIslandCoverPanelParameterSelector,
  (useCoverPanels, materialParameter, thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, coverPanelParameter) => {
    if (!useCoverPanels || useCoverPanels === undefined){
      return List();
    }

    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const materialValue = materialParameter.get('value');
    // console.log("materialValue.get('coverpanels') is: ", materialValue.get('coverpanels'));
    if (materialParameter.get('value') === null) {
      return List();
    } else if (!materialSelected) {
      return materialValue.get('coverpanels');
    }
    return kitchenIslandCoverPanelsForMaterial(materialParameter.get('value'), thicknessParameter, shapeParameter, sinkModelParameter, edgeParameter, coverPanelParameter);
  }
);


export const availableEdgesSelector = createSelector(
  optionsSelector,
  parameters.materialParameterSelector,
  parameters.expressionParameterSelector,
  parameters.overhangParameterSelector,
  isExpressionDecentralizedSelector,
  parameters.selectedThicknessSelector,
  (options, materialParameter, expressionParameter, overhangParameter, isDecentralized, thicknessParameter) => {

    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!materialSelected) {
      return options.get('edges');
    }
    return getAvailableEdges(options, materialParameter, expressionParameter, overhangParameter,isDecentralized, thicknessParameter);
  }
);

export const availableKitchenIslandEdgesSelector = createSelector(
  optionsSelector,
  parameters.kitchenIslandMaterialParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.kitchenIslandExpressionParameterSelector,
  parameters.kitchenIslandOverhangParameterSelector,
  isKitchenIslandExpressionDecentralizedSelector,
  parameters.selectedKitchenIslandThicknessSelector,
  (options, materialParameter, kitchenIslandParameter, kitchenIslandExpressionParameter, overhangParameter,isDecentralized, thicknessParameter) => {
    const kitchenIslandSelected = kitchenIslandParameter.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    if (!materialSelected || !kitchenIslandSelected) {
      return options.get('edges');
    }
    return getAvailableEdges(options, materialParameter, kitchenIslandExpressionParameter, overhangParameter,isDecentralized, thicknessParameter);
  }
);

const getListOfItemsToIgnore = (selectedMaterialValue) => {
  const expToIgnore = {};
  selectedMaterialValue.get('edges').map((ignoreExp) => {
    expToIgnore[ignoreExp.get('value')] = [];
    ignoreExp.get('ignoreExpression').find((item) => {
      if (item !== undefined) {
        expToIgnore[ignoreExp.get('value')].push(item.get('ItemNo'));
      }
    });
  });
  return expToIgnore;
}

const getExpressionsToIgnore = (selectedMaterialValue, expressionParameter) => {
  let ignoreExpMaterial;
  const selectedExpressionItemNo = expressionParameter.getIn(['value', 'itemNo']);
  const itemNos = getListOfItemsToIgnore(selectedMaterialValue);

  Object.keys(itemNos).forEach((key) => {
    if (itemNos[key].includes(selectedExpressionItemNo)) {
      ignoreExpMaterial = key;
    }
  });
  return ignoreExpMaterial;
};

const worktopThicknessAllowedWithEdge = (selectedMaterial, edgeOption, selectedThickness) => {
  if (isEdgeIncluded(selectedMaterial)) {
    const edgeWithConfig = selectedMaterial.get('edges').find((edge) => {
      if (edge.get('value') === edgeOption.get('id')) {
        return edge;
      }
    });
    const edgeAllowedWorktopThicknesses = edgeWithConfig && edgeWithConfig.get('allowedThicknesses');
    if (edgeWithConfig && edgeAllowedWorktopThicknesses && edgeAllowedWorktopThicknesses.size > 0) {
      return edgeAllowedWorktopThicknesses.find((thickness) => (parseInt(thickness) === parseInt(selectedThickness)));
    }
  }
  return true;
}

const getAvailableEdges = (options, materialParameter, expressionParameter, overhangParameter, isDecentralized, thicknessParameter) => {
  const selectedMaterialValue = materialParameter.get('value');
  const selectedMaterial = materialParameter.getIn(['value', 'id']);
  const selectedExpressionValue = expressionParameter.get('value');
  const selectedOverhang = overhangParameter.getIn(['value','id']);  
  const selectedThickness = thicknessParameter && thicknessParameter.get('thickness');
  const thicknessName = thicknessDisplayName(selectedThickness);

  if (selectedMaterialValue === null) {
    return options.get('edges');
  }
  return options.get('edges').map((edgeOption) => {
    const conflicts = [];
    const edgeValue = edgeOption.get('id');
    if (isEdgeIncluded(selectedMaterialValue)) {
      if (selectedMaterialValue && !edgeMaterialAllowed(selectedMaterialValue, edgeValue)) {
        conflicts.push(selectedMaterialValue.get('displayName'));
      }
    }
    if(!worktopThicknessAllowedWithEdge(selectedMaterialValue, edgeOption, selectedThickness)){
      conflicts.push(thicknessName); 
    }
    if(isEdgeIncluded(selectedMaterialValue)){
      if (edgeMaterialAllowed(selectedMaterialValue, edgeValue) && edgeValue === getExpressionsToIgnore(selectedMaterialValue, expressionParameter)) {
        conflicts.push(selectedExpressionValue.get('displayName'));
      }
    }

    if(selectedOverhang === 'waterfall' && isQuartzAndCeramicDecentralized(isDecentralized,selectedMaterial)){ 
      if(edgeValue !== 'chamfered'){
        conflicts.push(overhangMessages.waterfall);
      }
    }
    return (conflicts.length > 0) ? edgeOption.set('conflicts', List(conflicts)) : edgeOption;
  });
};

export const availableThicknessesSelector = createSelector(
  parameters.materialParameterSelector,
  optionsSelector,
  parameters.expressionParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.selectedThicknessSelector,
  parameters.edgeParameterSelector,
  parameters.selectedEdgeSelector,
  (materialParameter, options, expressionParameter, sinkModelParameter, selectedThickness, edgeParameter, selectedEdge) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;

    const selectedExpressionValue = expressionParameter.get('value');
    const selectedSinkModelValue = sinkModelParameter.get('value');
    const allowedThicknesses = selectedEdge && selectedEdge.get('allowedThicknesses');
    const edgeOption = edgeParameter.get('value');

    if (!materialSelected) {
      return options.get('thicknesses');
    }
    return options.get('thicknesses').map((thicknessOption) => {
      const conflicts = [];
      const thicknessOptionId = thicknessOption.get('id');
      if (selectedExpressionValue && !thicknessAvailableInExpression(selectedExpressionValue, thicknessOptionId)) {
        conflicts.push(selectedExpressionValue.get('displayName'));
      }

      if (allowedThicknesses && allowedThicknesses.size > 0) {
        const isThicknessAllowed = allowedThicknesses.find((thickness) => (parseInt(thickness) === parseInt(thicknessOptionId)));
        if(!isThicknessAllowed){
          conflicts.push(edgeOption.get('displayName'));
        }
      }

      if (selectedSinkModelValue && !thicknessAvailableWithSinkModel(selectedSinkModelValue, thicknessOptionId)) {
        conflicts.push(selectedSinkModelValue.get('displayName'));
      }

      if (conflicts.length > 0) {
        return thicknessOption.set('conflicts', List(conflicts));
      } else {
        return thicknessOption;
      }
    });
  }
);

export const availableKitchenIslandThicknessesSelector = createSelector(
  optionsSelector,
  parameters.kitchenIslandExpressionParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.selectedKitchenIslandThicknessSelector,
  parameters.kitchenIslandEdgeParameterSelector,
  parameters.selectedKitchenIslandEdgeSelector,
  (options, expressionParameter, kitchenIsland, selectedThickness, edgeParameter, selectedEdge) => {
    const kitchenIslandSelected = kitchenIsland.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    if (!kitchenIslandSelected) {
      return options.get('thicknesses');
    }

    const allowedThicknesses = selectedEdge && selectedEdge.get('allowedThicknesses');
    const edgeOption = edgeParameter.get('value');

    const selectedExpressionValue = expressionParameter.get('value');

    return options.get('thicknesses').map((thicknessOption) => {
      const conflicts = [];
      const thicknessOptionId = thicknessOption.get('id');

      if (selectedExpressionValue && !thicknessAvailableInExpression(selectedExpressionValue, thicknessOptionId)) {
        conflicts.push(selectedExpressionValue.get('displayName'));
      }

      if (allowedThicknesses && allowedThicknesses.size > 0) {
        const isThicknessAllowed = allowedThicknesses.find((thickness) => (parseInt(thickness) === parseInt(thicknessOptionId)));
        if(!isThicknessAllowed){
          conflicts.push(edgeOption.get('displayName'));
        }
      }

      if (conflicts.length > 0) {
        return thicknessOption.set('conflicts', List(conflicts));
      } else {
        return thicknessOption;
      }
    });
  }
);

export const availableSplashbacksSelector = createSelector(
  optionsSelector,
  parameters.materialParameterSelector,
  parameters.wallLocationParameterSelector,
  (options, material, wallLocation) => {
    const selectedMaterial = material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;

    const selectedMaterialValue = material.get('value');
    const selectedWallLocationValue = wallLocation.get('value');
    if (!selectedMaterialValue || !selectedWallLocationValue) return options.get('splashbacks');
    return options.get('splashbacks').map((splashback) => {
      const conflicts = [];
      if (selectedMaterial && splashback.get('id') === WALL_EDGING_STRIP_ID && !selectedMaterialValue.get('wallEdgingStrip')) {
        conflicts.push(selectedMaterialValue.get('displayName'));
      }
      if (selectedMaterial && splashback.get('id') === WALL_EDGING_STRIP_CUSTOMIZABLE_ID && !selectedMaterialValue.get('wallEdgingStripCustomizable')) {
        conflicts.push(selectedMaterialValue.get('displayName'));
      }
      if (splashback.get('id') !== NO_SPLASHBACK_ID && selectedWallLocationValue.get('id') === 'no-walls') {
        conflicts.push(selectedWallLocationValue.get('displayName'));
      }
      return (conflicts.length > 0) ? splashback.set('conflicts', List(conflicts)) : splashback;
    });
  }
);

export const availableWallEdgingStripMaterialsSelector = createSelector( 
  optionsSelector,
  (options) => {
    return options.get('wallEdgingStrip').map((wallEdgingStripMaterial) => {
     return wallEdgingStripMaterial;
    });
  }
);

export const availableWallEdgingStripCustomizableMaterialsSelector = createSelector( 
  optionsSelector,
  (options) => {
    return options.get('wallEdgingStripCustomizable').map((wallEdgingStripCustomizableMaterial) => {
      return wallEdgingStripCustomizableMaterial;
    });
  }
);

export const availableKitchenIslandSplashbacksSelector = createSelector(
  optionsSelector,
  parameters.kitchenIslandWallLocationParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.kitchenIslandSplashbackSelector,
  (options, wallLocation, kitchenIsland, kitchenIslandSplashback ) => {
    const selectedWallLocationValue = wallLocation.get('value');
    const kitchenIslandSelected = kitchenIsland.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    return options.get('splashbacks').map((splashback) => {
      const conflicts = [];
        if (kitchenIslandSelected && kitchenIslandSplashback && splashback.get('id') !== NO_SPLASHBACK_ID && selectedWallLocationValue.get('id') === 'no-walls') {
          conflicts.push(selectedWallLocationValue.get('displayName'));
        }
      return (conflicts.length > 0) ? splashback.set('conflicts', List(conflicts)) : splashback;
    });
  }
);

export const availableWallPanelMaterialsSelector = createSelector(
  noWallpanelsAvailableSelector,
  optionsSelector,
  createFieldSelector(wallPanelOperationParameters, 'parameter'),
  (noWallPanelMaterials, options, wallPanelOperations) => {
    return options.get('wallPanels').map((wallPanelMaterial) => {
      const conflicts = [];
      if (noWallPanelMaterials !== undefined && noWallPanelMaterials === false) {
        //console.log('noWallPanelMaterials: ',noWallPanelMaterials);
        wallPanelOperations.forEach((operation) => {
          if (!wallPanelMaterial.getIn(['operations', operation.operation]) && operation.parameter.get('value') > 0) {
            conflicts.push(operation.message);
          }
        });
      }
      return (conflicts.length > 0) ? wallPanelMaterial.set('conflicts', List(conflicts)) : wallPanelMaterial;
    });
  }
);

export const availableWallPanelExpressionsSelector = createSelector(
  parameters.wallPanelMaterialParameterSelector,
  (selectedWallPanelMaterial) => {
    return selectedWallPanelMaterial.getIn(['value', 'expressions']) || List();
  }
);

export const availableSinkMaterialsSelector = createSelector(
  optionsSelector,
  (options) => options.get('sinks')
);

export const availableSinkModelsSelector = createSelector(
  parameters.sinkMaterialParameterSelector,
  parameters.sinkFasteningParameterSelector,
  parameters.materialParameterSelector,
  parameters.drainerGroovesParameterSelector,
  parameters.selectedThicknessSelector,
  parameters.plannedShapeParameterSelector,
  (sinkMaterialParameter, sinkFasteningParameter, material, drainerGrooves, selectedThickness, plannedShape) => {
    if (!sinkMaterialParameter.get('value')) {
      return List();
    }
    const materialSelected = material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;

    const models = sinkMaterialParameter.getIn(['value', 'models']);
    const materialId = material.getIn(['value', 'id']);

    if (!materialId || sinkMaterialParameter.getIn(['value', 'id']) === 'noSink') {
      return models;
    }

    const fasteningId = sinkFasteningParameter.getIn(['value', 'id']);
    const thickness = selectedThickness && selectedThickness.get('thickness');
    const thicknessName = thicknessDisplayName(thickness);

    const shapeMeasurements = plannedShape.get('measurements').toJS();

    return models.map((sinkModel) => {
      const conflicts = [];

      if (sinkModel.get('hasDrainer') && drainerGrooves.getIn(['value', 'id']) !== 'none') {
        conflicts.push(drainerGrooves.getIn(['value', 'displayName']));
      }

      const undergluedFastenings = sinkModel.getIn(['fastenings', 'underGlued']) || List();
      const underglueInCurrentMaterial = undergluedFastenings.countBy((s) => s.get('worktopMaterialId') === materialId);
      const currentMaterial = underglueInCurrentMaterial.get(true) || 0;

      if (fasteningId === 'underGlued' && currentMaterial === 0 && sinkModel.get('fastenings').size <= 1) {
        conflicts.push(material.getIn(['value', 'displayName']));
      }
      if (fasteningId && sinkFasteningParameter.get('selectionType') === SELECTION_TYPE_USER) {
        if (!sinkModel.getIn(['fastenings', fasteningId])) {
          conflicts.push(sinkFasteningParameter.getIn(['value', 'displayName']));
        }
      }
      const thicknessCondition = sinkModel.getIn(['configuration', 'thicknessCondition']);
      if (thickness && thicknessCondition) {
        if (!thicknessCondition({ thickness })) {
          conflicts.push(thicknessName);
        }
      }

      const hasOneFastening = sinkModel.get('fastenings').size === 1;
      const fitBetweenFastening = sinkModel.getIn(['fastenings', 'fitBetween']);
      const depthCondition = sinkModel.getIn(['configuration', 'depthCondition']);
      let lengthCondition;

      if (fasteningId === 'topMounted' && fitBetweenFastening) { //if selected fastening is topMounted but sink has fitbeetween fastening
        conflicts.push(sinkFasteningParameter.getIn(['value', 'displayName']));
      }
      if (fasteningId === "fitBetween" && !fitBetweenFastening) { //if selected fastening is fitbetween but the sinkModel doesn´t have this fastening
        conflicts.push(sinkFasteningParameter.getIn(['value', 'displayName']));
      }
      if (plannedShape.getIn(['value', 'id']) === 'irregular' && hasOneFastening && fitBetweenFastening) {
        conflicts.push([parameterMessages.shape, ': ', plannedShape.getIn(['value', 'displayName']).toJS()]);
      } else if (fitBetweenFastening && (fasteningId === 'fitBetween' || hasOneFastening)) {
        lengthCondition = getSinkLengthCondition(fitBetweenFastening.getIn([0, 'width']));
      }
      if (materialSelected) {
        if ((depthCondition || lengthCondition) && validateSinkConditions(plannedShape.getIn(['value', 'id']), shapeMeasurements, { depthCondition, lengthCondition })) {
          conflicts.push([parameterMessages.shape, ' ', parameterMessages.measurements]);
        }
      }
      if (!materialSelected) {
        conflicts.push(parameterMessages.material);
      }

      return (conflicts.length > 0) ? sinkModel.set('conflicts', List(conflicts)) : sinkModel;
    });
  }
);

export const availableSinkFasteningsSelector = createSelector(
  optionsSelector,
  parameters.materialParameterSelector,
  parameters.sinkMaterialParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.drainerGroovesParameterSelector,
  parameters.plannedShapeParameterSelector,
  parameters.selectedThicknessSelector,
  (options, material, sinkMaterial, sinkModel, drainerGrooves, plannedShape, selectedThickness) => {
    if (!sinkMaterial.get('value') || !sinkModel.get('value') || !material.get('value') || sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return options.get('sinkFastenings');
    }
    const plannedShapeValue = plannedShape.get('value');
    const shapeSinkFasteningCondition = plannedShapeValue && plannedShapeValue.get('sinkFasteningCondition');

    const availableFastenings = sinkModel.getIn(['value', 'fastenings']);
    return options.get('sinkFastenings').map((fastening) => {
      const conflicts = [];
      const fasteningId = fastening.get('id');
      const fastenings = availableFastenings.get(fasteningId);
      
      if (fastenings) {
        const materialId = material.getIn(['value', 'id']);
        const models = sinkMaterial.getIn(['value', 'models']);

        const shapeMeasurements = plannedShape.get('measurements').toJS();
        const fitBetweenFastening = sinkModel.getIn(['value', 'fastenings', 'fitBetween']);
        const depthCondition = sinkModel.getIn(['configuration', 'depthCondition']);
        let lengthCondition;
        if (fasteningId === 'fitBetween') {
          if (fitBetweenFastening) {
            lengthCondition = getSinkLengthCondition(fitBetweenFastening.getIn([0, 'width']));
          }
          if ((depthCondition || lengthCondition) && validateSinkConditions(plannedShape.getIn(['value', 'id']), shapeMeasurements, { depthCondition, lengthCondition })) {
            conflicts.push([parameterMessages.shape, ' ', parameterMessages.measurements]);
          }
          const thickness = selectedThickness && selectedThickness.get('thickness');
          const thicknessName = thicknessDisplayName(thickness);
          const thicknessCondition = sinkModel.getIn(['value', 'configuration', 'thicknessCondition']);
          if (thickness && thicknessCondition) {
            if (!thicknessCondition({ thickness })) {
              conflicts.push(material.getIn(['value', 'displayName']));
              conflicts.push(thicknessName);
            }
          }
        }
        if (fasteningId === 'underGlued' && !fastenings.find((s) => s.get('worktopMaterialId') === materialId)) {
          conflicts.push(material.getIn(['value', 'displayName']));
        } else if (fasteningId !== 'underGlued' && drainerGrooves.getIn(['value']) && drainerGrooves.getIn(['value', 'id']) !== 'none') {
          conflicts.push(drainerGrooves.getIn(['value', 'displayName']));
        }
      } else {
        conflicts.push(sinkModel.getIn(['value', 'displayName']));
      }

      if (shapeSinkFasteningCondition && !shapeSinkFasteningCondition(fasteningId)) {
        conflicts.push([parameterMessages.shape, ': ', plannedShapeValue.get('displayName').toJS()]);
      }

      return conflicts.length ? fastening.set('conflicts', List(conflicts)) : fastening;
    });
  }
);

export const availableDrainerGroovesSelector = createSelector(
  optionsSelector,
  parameters.materialParameterSelector,
  parameters.sinkMaterialParameterSelector,
  parameters.sinkModelParameterSelector,
  parameters.sinkFasteningParameterSelector,
  (options, material, sinkMaterial, sinkModel, sinkFastening) => {
    const drainerGrooves = options.get('drainerGrooves');
    const materialValue = material.get('value');
    const sinkModelValue = sinkModel.get('value');
    const sinkFasteningValue = sinkFastening.get('value');
    const hasUnavailable = !materialValue || !sinkModelValue || !sinkFasteningValue;

    if (hasUnavailable || sinkMaterial.getIn(['value', 'id']) === 'noSink') {
      return drainerGrooves;
    }

    return drainerGrooves.map((option) => {
      const conflicts = [];

      if (option.get('id') === 'none') {
        return option;
      }

      if (!materialValue.getIn(['operations', 'drainerGrooves'])) {
        conflicts.push(materialValue.get('displayName'));
      }

      if (sinkModelValue.get('hasDrainer')) {
        conflicts.push(sinkModelValue.get('displayName'));
      }

      if (sinkFasteningValue.get('id') !== 'underGlued') {
        conflicts.push(sinkFasteningValue.get('displayName'));
      }

      return (conflicts.length > 0) ? option.set('conflicts', List(conflicts)) : option;
    });
  }
);


export const availableKitchenIslandMaterialsSelector = createSelector(
  optionsSelector,
  parameters.kitchenIslandEdgeParameterSelector,
  parameters.selectedKitchenIslandThicknessSelector,
  parameters.kitchenIslandThicknessParameterSelector,
  parameters.kitchenIslandParameterSelector,
  parameters.kitchenIslandOverhangParameterSelector,
  createFieldSelector(kitchenIslandOperationParameters, 'parameter'),
  parameters.kitchenIslandExpressionParameterSelector,
  (options, edgeParameter, selectedThickness, thicknessParameter, kitchenIsland, overhangParameter, kitchenIslandOperations, expressionParameter) => {
    const edgeOption = edgeParameter.get('value');
    const kitchenIslandSelected = kitchenIsland.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    const overhang = overhangParameter.get('value');
    const selectedEdgeValue = edgeOption && edgeOption.get('id');

    if (!edgeOption || !kitchenIslandSelected) return options.get('materials');

    return options.get('materials').map((material) => {
      const conflicts = [];
      if (material.get('id') === NO_WORKTOP_MATERIAL) {
        return material;
      }
      if(isEdgeIncluded(material)){
        if (!edgeMaterialAllowed(material, selectedEdgeValue)) {
          conflicts.push(edgeOption.get('displayName'));
        }
      }
      if(material.get('id') === 'quartz' || material.get('id') === 'ceramic'){               
        if((selectedEdgeValue !== 'chamfered') && (overhang.get('id') === 'waterfall')){                    
          selectedEdgeValue === 'same-color' ?  conflicts.push(edgeMessages.straight) : conflicts.push(edgeOption.get('displayName')); 
          conflicts.push(overhang.get('displayName')); 
        }
      }
      const allowedMaterials = overhang.get('allowedMaterials');
      if (allowedMaterials && !allowedMaterials.includes(material.get('id'))) {
        conflicts.push(overhang.get('displayName'));
      }

      kitchenIslandOperations.forEach((operation) => {
        if (!material.getIn(['operations', operation.operation]) && operation.parameter.get('value') > 0) {
          conflicts.push([parameterMessages.kitchenIsland, ': ', operation.message]);
        }
      });

      const selectedThicknessOrId = (selectedThickness && selectedThickness.get('thickness')) || thicknessParameter.getIn(['value', 'id']);
      const thicknessName = thicknessDisplayName(selectedThicknessOrId);
      const expressions = kitchenIslandExpressionsForMaterial(material, thicknessParameter, expressionParameter);
      const noExpressionsAvailableWithThickness = expressions.every((expr) => {
        return expr.get('conflicts') && expr.get('conflicts').some((c) => _.isEqual(c, thicknessName));
      });

      if (noExpressionsAvailableWithThickness) {
        conflicts.push(thicknessName);
      }
      return (conflicts.length > 0) ? material.set('conflicts', List(conflicts)) : material;
    });
  });

export const availableOverhangSidesSelector = createSelector(
  parameters.materialParameterSelector,
  parameters.plannedShapeParameterSelector,
  parameters.wallLocationParameterSelector,
  parameters.overhangParameterSelector,
  (materialParameter, plannedShape, wallLocationParameter, overhangParameter) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const overhangSides = plannedShape.getIn(['value', 'overhangSides']);
    const wallLocation = wallLocationParameter.get('value');
    const overhang = overhangParameter.get('value');
    if (!materialSelected) {
      return overhangSides;
    }
    if (!plannedShape.get('value')) {
      return List();
    } else if (!wallLocation || !overhang || overhang.get('id') === NO_OVERHANG_ID) {
      return overhangSides;
    }
    return overhangSides.map((side) => {
      const conflicts = [];
      const hasConflictWithWallLocation = overhangSideHasConflictWithWallLocation(wallLocation)(side);

      if (hasConflictWithWallLocation) {
        conflicts.push(wallLocation.get('displayName'));
      }
      return (conflicts.length > 0) ? side.set('conflicts', List(conflicts)) : side;
    });
  }
);

const overhangSideHasConflictWithWallLocation = (wallLocation) => (side) => {
  const walls = wallLocation.get('drawWalls') || wallLocation.get('wallLocations');
  return side.get('dimensions').some((dimension) => walls.includes(dimension.get('side') || dimension.get('depth')));
};

export const availableOverhangsSelector = createSelector(
  parameters.plannedShapeParameterSelector,
  optionsSelector,
  parameters.materialParameterSelector,
  availableOverhangSidesSelector,
  parameters.wallLocationParameterSelector,
  parameters.edgeParameterSelector,
  isExpressionDecentralizedSelector,
  (plannedShape, options, material, availableOverhangSides, wallLocation, edgeParameter, isDecentralized) => {
    const selectedMaterial = material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const overhangs = options.get('overhangs');

    if (!selectedMaterial) {
      return overhangs;
    }
    const materialValue = material.get('value');
    const wallLocationValue = wallLocation.get('value');
    const hasUnavailable = !materialValue || !wallLocationValue;
    if (hasUnavailable) {
      return overhangs;
    }

    const materialId = materialValue.get('id');
    const materialDisplayName = materialValue.get('displayName');
    return overhangs.map((option) => {
      const conflicts = [];
      if (option.get('id') === NO_OVERHANG_ID) {
        return option;
      }
      if (isEdgeIncluded(material.get('value'))) {
        if (isQuartzAndCeramicDecentralized(isDecentralized, material.getIn(['value', 'id']))) {
          const edgeOption = edgeParameter.get('value');
          if ((edgeOption.get('id') !== 'chamfered') && (option.get('id') === 'waterfall')) {
            edgeOption.get('id') === 'same-color' ? conflicts.push(edgeMessages.straight) : conflicts.push(edgeOption.get('displayName'));
          }
        }
      }
      if (!option.get('allowedMaterials').includes(materialId)) {
        conflicts.push(materialDisplayName);
      }
      if (availableOverhangSides) {
        if (availableOverhangSides.every(overhangSideHasConflictWithWallLocation(wallLocationValue))) {
          conflicts.push(wallLocationValue.get('displayName'));
        }
      }
      return (conflicts.length > 0) ? option.set('conflicts', List(conflicts)) : option;
    });
  }
);

export const availableKitchenIslandOverhangSidesSelector = createSelector(
  optionsSelector,
  parameters.kitchenIslandMaterialParameterSelector,
  parameters.kitchenIslandWithMeasurementsSelector,
  parameters.kitchenIslandWallLocationParameterSelector,
  parameters.kitchenIslandOverhangParameterSelector,
  (options, materialParameter, plannedShape, wallLocationParameter, overhangParameter) => {
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const overhangSides = options.getIn(['kitchenIslands', 1, 'overhangSides']);
    const wallLocation = wallLocationParameter.get('value');
    const overhang = overhangParameter.get('value');

    if (!materialSelected) {
      return overhangSides;
    }
    if (!plannedShape.get('value')) {
      return List();
    } else if (!wallLocation || !overhang || overhang.get('id') === NO_OVERHANG_ID) {
      return overhangSides;
    }
    return overhangSides.map((side) => {
      const conflicts = [];
      const hasConflictWithWallLocation = overhangSideHasConflictWithWallLocation(wallLocation)(side);

      if (hasConflictWithWallLocation) {
        conflicts.push(wallLocation.get('displayName'));
      }
      return (conflicts.length > 0) ? side.set('conflicts', List(conflicts)) : side;
    });
  }
);

export const availableKitchenIslandOverhangsSelector = createSelector(
  parameters.kitchenIslandWithMeasurementsSelector,
  optionsSelector,
  parameters.kitchenIslandMaterialParameterSelector,
  availableKitchenIslandOverhangSidesSelector,
  parameters.kitchenIslandWallLocationParameterSelector,
  parameters.kitchenIslandEdgeParameterSelector,
  isKitchenIslandExpressionDecentralizedSelector,
  (plannedShape, options, material, availableOverhangSides, wallLocation, edgeParameter, isDecentralized) => {
    const selectedMaterial = material.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const overhangs = options.get('kitchenIslandOverhangs');
    const edgeOption = edgeParameter.get('value');
    if (!selectedMaterial) {
      return overhangs;
    }
    const materialValue = material.get('value');
    const wallLocationValue = wallLocation.get('value');
    const hasUnavailable = !materialValue || !wallLocationValue;
    if (hasUnavailable) {
      return overhangs;
    }

    const materialId = materialValue.get('id');
    const materialDisplayName = materialValue.get('displayName');
    return overhangs.map((option) => {
      const conflicts = [];
      if (option.get('id') === NO_OVERHANG_ID) {
        return option;
      }
      if (isEdgeIncluded(material.get('value'))) {
        if (isQuartzAndCeramicDecentralized(isDecentralized, materialId)) {
          if ((edgeOption.get('id') !== 'chamfered') && (option.get('id') === 'waterfall')) {
            edgeOption.get('id') === 'same-color' ? conflicts.push(edgeMessages.straight) : conflicts.push(edgeOption.get('displayName'));
          }
        }
      }
      if (!option.get('allowedMaterials').includes(materialId)) {
        conflicts.push(materialDisplayName);
      }
      if (availableOverhangSides) {
        if (availableOverhangSides.every(overhangSideHasConflictWithWallLocation(wallLocationValue))) {
          conflicts.push(wallLocationValue.get('displayName'));
        }
      }
      return (conflicts.length > 0) ? option.set('conflicts', List(conflicts)) : option;
    });
  }
);

export const kitchenIslandExpressionsForMaterial = (material, thicknessParameter, edgeParameter, expressionParameter) => {
  const selectedThicknessOptionValue = thicknessParameter.get('value');
  const expressions = material.get('expressions');
  if (!expressions) {
    return List();
  }
  return expressions.map((expression) => {
    const conflicts = [];
    if (isEdgeIncluded(material)) {
      const edgeValue = edgeParameter.get('value').get('id');
      const expToIgnore = getListOfItemsToIgnore(material);
      Object.keys(expToIgnore).forEach((key) => {
        if (edgeMaterialAllowed(material, edgeValue) && (expToIgnore[key].includes(expression.get('itemNo')) && edgeValue === key)) {
          conflicts.push(edgeValue);
        }
      });
    }
    if (selectedThicknessOptionValue && thicknessParameter.get('selectionType') === SELECTION_TYPE_USER && !thicknessAvailableInExpression(expression, selectedThicknessOptionValue.get('id'))) {
      conflicts.push(thicknessDisplayName(selectedThicknessOptionValue.get('id')));
    }
    return (conflicts.length > 0) ? expression.set('conflicts', List(conflicts)) : expression;
  });
};
export const availableWallLocationsSelector = createSelector(
  parameters.materialParameterSelector,
  parameters.plannedShapeParameterSelector,
  parameters.splashbackParameterSelector,
  parameters.overhangSideParameterSelector,
  parameters.overhangParameterSelector,
  availableOverhangSidesSelector,
  (materialParameter, plannedShape, splashback, overhangSideParameter, overhangParameter, availableOverhangSides) => {
    const shape = plannedShape.get('value');
    const overhangSide = overhangSideParameter.get('value');
    const splashbackValue = splashback.get('value');
    const overhang = overhangParameter.get('value');
    const materialId = materialParameter.getIn(['value', 'id']);
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const splashbackSelected = splashback.getIn(['value', 'id']) !== NO_SPLASHBACK_ID;
    if (shape) {
      if (!materialSelected && splashbackSelected && splashbackValue) {
        return shape.get('wallLocations').map((wallLocation) => {
          const conflicts = [];
          if (wallLocation.get('id') === 'no-walls') {
            conflicts.push(splashbackValue.get('displayName'));
          }
          return (conflicts.length > 0) ? wallLocation.set('conflicts', List(conflicts)) : wallLocation;
        });
      }
      if (!overhangSide || !splashbackValue || !overhang || !materialSelected) {
        return shape.get('wallLocations');
      }
      return shape.get('wallLocations').map((wallLocation) => {
        const conflicts = [];
        if (splashbackValue.get('id') !== NO_SPLASHBACK_ID && wallLocation.get('id') === 'no-walls') {
          conflicts.push(splashbackValue.get('displayName'));
        }
        if (overhang.get('id') !== NO_OVERHANG_ID && (availableOverhangSides.every(overhangSideHasConflictWithWallLocation(wallLocation)) ||
          (overhangSideHasConflictWithWallLocation(wallLocation)(overhangSide) && overhangSideParameter.get('selectionType') === SELECTION_TYPE_USER))) {
          conflicts.push(overhangSide.get('displayName'));
        }
        if (!materialSelected) {
          conflicts.push(materialId);
        }
        return (conflicts.length > 0) ? wallLocation.set('conflicts', List(conflicts)) : wallLocation;
      });
    } else {
      return List();
    }
  }
);


export const availableKitchenIslandWallLocationsSelector = createSelector(
  parameters.kitchenIslandMaterialParameterSelector,
  parameters.kitchenIslandWithMeasurementsSelector,
  parameters.splashbackParameterSelector,
  parameters.kitchenIslandOverhangSideParameterSelector,
  parameters.kitchenIslandOverhangParameterSelector,
  availableKitchenIslandOverhangSidesSelector,
  parameters.kitchenIslandSplashbackSelector,
  (materialParameter, plannedShape, splashback, overhangSideParameter, overhangParameter, availableOverhangSides, kitchenIslandSplashback) => {
    const shape = plannedShape.get('value');
    const overhangSide = overhangSideParameter.get('value');
    const splashbackValue = splashback.get('value');
    const overhang = overhangParameter.get('value');
    const materialId = materialParameter.getIn(['value', 'id']);
    const materialSelected = materialParameter.getIn(['value', 'id']) !== NO_WORKTOP_MATERIAL;
    const splashbackSelected = splashback.getIn(['value', 'id']) !== NO_SPLASHBACK_ID;
    const kitchenIslandSelected = plannedShape.getIn(['value', 'id']) !== NO_KITCHEN_ISLAND_ID;
    if (shape) {
      if (!overhangSide || !splashbackValue || !overhang || !materialSelected || !kitchenIslandSelected) {
        return shape.get('kitchenIslandWallLocations');
      }
      return shape.get('kitchenIslandWallLocations').map((wallLocation) => {
        const conflicts = [];
        if (splashbackValue.get('id') !== NO_SPLASHBACK_ID && wallLocation.get('id') === 'no-walls' && kitchenIslandSplashback) {
          conflicts.push(splashbackValue.get('displayName'));
        }
        if (overhang.get('id') !== NO_OVERHANG_ID && (availableOverhangSides.every(overhangSideHasConflictWithWallLocation(wallLocation)) ||
          (overhangSideHasConflictWithWallLocation(wallLocation)(overhangSide) && overhangSideParameter.get('selectionType') === SELECTION_TYPE_USER))) {
          conflicts.push(overhangSide.get('displayName'));
        }
        if (!materialSelected) {
          conflicts.push(materialId);
        }
        return (conflicts.length > 0) ? wallLocation.set('conflicts', List(conflicts)) : wallLocation;
      });
    } else {
      return List();
    }
  }
);
